import axios from 'axios';
import { constant } from '../constant';
import { toast } from 'react-toastify';

// ========================================
// VARIABLES GLOBALES PARA CONTROL DE SESIÓN
// ========================================
let isRefreshingSession = false;
let isRedirecting = false;
let failedRequestsQueue = [];

// ========================================
// FUNCIONES HELPER
// ========================================

// Procesar cola de peticiones fallidas
const processQueue = (error = null) => {
  failedRequestsQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedRequestsQueue = [];
};

// Limpiar sesión completamente
const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('hotelData');
  sessionStorage.removeItem('token');
};

// Verificar si ya hay un token válido
const hasValidToken = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return !!token;
};

// ========================================
// INTERCEPTOR DE RESPUESTAS (MANEJO DE 401)
// ========================================
axios.interceptors.response.use(
  (response) => {
    // Respuesta exitosa, continuar normalmente
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Si el error NO es 401, rechazar inmediatamente
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    // ============================================
    // MANEJO DE ERROR 401 (Token expirado/inválido)
    // ============================================

    // Si ya estamos redirigiendo, cancelar todas las peticiones pendientes
    if (isRedirecting) {
      console.log('⏭️ Redirección en progreso, cancelando petición...');
      return Promise.reject(new Error('Redirecting to login'));
    }

    // Marcar que estamos procesando el 401
    if (!isRefreshingSession) {
      isRefreshingSession = true;
      isRedirecting = true;

      console.log('🔐 Sesión expirada detectada, iniciando limpieza...');

      // Limpiar storage
      clearSession();

      // Procesar cola de peticiones (rechazarlas todas)
      processQueue(error);

      // Mostrar notificación UNA SOLA VEZ
      toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
        toastId: 'session-expired-401',
        autoClose: 3000,
        position: 'top-right'
      });

      // Esperar un momento antes de redirigir (para que los componentes se enteren)
      setTimeout(() => {
        console.log('🔄 Redirigiendo al login...');
        window.location.href = '/login';
      }, 800);
    }

    // Agregar esta petición a la cola para rechazarla
    return new Promise((resolve, reject) => {
      failedRequestsQueue.push({ resolve, reject });
    });
  }
);

// ========================================
// INTERCEPTOR DE PETICIONES (AGREGAR TOKEN)
// ========================================
axios.interceptors.request.use(
  (config) => {
    // Si ya estamos redirigiendo, cancelar esta petición
    if (isRedirecting) {
      console.log('⏭️ Petición cancelada: redirección en progreso');
      return Promise.reject(new Error('Redirecting to login'));
    }

    // Agregar token si existe
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ========================================
// FUNCIONES API
// ========================================

export const postApi = async (path, data) => {
  try {
    let result = await axios.post(constant.baseUrl + path, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      }
    });
    if (result) {
      return result;
    } else {
      return false;
    }
  } catch (e) {
    console.error('❌ POST API Error:', e);
    if (e.response) console.error('❌ Response Data:', e.response.data);
    return e;
  }
};

export const patchApi = async (path, data) => {
  try {
    let result = await axios.patch(constant.baseUrl + path, data, {
      data: data?.roomNo,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      }
    });
    return result;
  } catch (e) {
    console.error(e);
    return e;
  }
};

export const deleteApi = async (path, id) => {
  try {
    let result = await axios.delete(constant.baseUrl + path + id, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      }
    });
    return result;
  } catch (e) {
    console.error(e);
    return e;
  }
};

export const deleteManyApi = async (path, data) => {
  try {
    let result = await axios.post(constant.baseUrl + path, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      }
    });
    if (result.data?.token && result.data?.token !== null) {
      localStorage.setItem('token', result.data?.token);
    }
    return result;
  } catch (e) {
    console.error(e);
    return e;
  }
};

export const getApi = async (path, id) => {
  try {
    // Si ya estamos redirigiendo, retornar datos vacíos en lugar de hacer la petición
    if (isRedirecting) {
      return { data: { reservationData: [] } };
    }

    if (id) {
      let result = await axios.get(constant.baseUrl + path + id, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        }
      });
      return result;
    } else {
      let result = await axios.get(constant.baseUrl + path, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        }
      });
      return result;
    }
  } catch (e) {
    console.error(e);
    // Si ya estamos redirigiendo, retornar datos vacíos
    if (isRedirecting) {
      return { data: { reservationData: [] } };
    }
    return e;
  }
};

export const putApi = async (path, data) => {
  try {
    let result = await axios.put(constant.baseUrl + path, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      }
    });
    return result;
  } catch (e) {
    if (e.response && e.response.data) {
      console.error('❌ Error del backend:', e.response.data.error);
    } else {
      console.error('❌ Error desconocido:', e.message);
    }
    return e;
  }
};

// ========================================
// RESETEAR ESTADO (útil después de login exitoso)
// ========================================
export const resetSessionState = () => {
  isRefreshingSession = false;
  isRedirecting = false;
  failedRequestsQueue = [];
  console.log('✅ Estado de sesión reseteado');
};
